import { Card, Col, ProgressBar, Row, Spinner } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import SubtleBadge from '@/components/common/SubtleBadge';
import SoftBadge from '@/components/common/SoftBadge';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import { toPersianDigit } from '@/helpers/utils';
import { KpiStatCard } from '@/app/TrackerAi/components/common/KpiStatCard';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import SemiCircleChartSkeleton from '@/app/common/components/skeleton/SemiCircleChartSkeleton';
import type {
  AiDetectorAnalysisData,
  EeatAnalysisData,
  GeoAnalysisData,
  SeoOverviewData,
  SeoPriorityTone,
  SeoStructureItem
} from '@/app/ContentTools/types/aiContentAnalysisApiTypes';
import { aiAnalysisDetailsPath } from '@/app/ContentTools/utils/aiAnalysisPaths';
import AiLikenessGauge from './AiLikenessGauge';
import AiSignalsPanel from './AiSignalsPanel';
import SeoScoreCard from './SeoScoreCard';
import { EeatScoreSummary } from './EeatScoreCard';
import { GeoScoreSummary } from './GeoScoreCard';
import {
  EeatDimensionsOverview,
  EeatDimensionsOverviewPending
} from './EeatDimensionsOverview';
import {
  GeoDimensionsOverview,
  GeoDimensionsOverviewPending
} from './GeoDimensionsOverview';
import {
  OverviewCardTitle,
  OverviewItemTitle,
  OverviewSectionTitle
} from './OverviewSectionTitle';

export type AiAnalysisOverviewProps = {
  seo: SeoOverviewData | null;
  detector: AiDetectorAnalysisData;
  eeat?: EeatAnalysisData | null;
  geo?: GeoAnalysisData | null;
  showDetection?: boolean;
  isDetectionPending?: boolean;
  isEeatPending?: boolean;
  isGeoPending?: boolean;
};

const TOP_SEO_CRITERIA = [
  'کیفیت محتوا',
  'عنوان متا',
  'توضیحات متا',
  'تگ H1',
  'ساختار آدرس صفحه',
  'متن جایگزین تصاویر (Alt)'
];

const SEO_CRITERIA_META: Record<string, { icon: IconProp; helpHtml: string }> = {
  'کیفیت محتوا': {
    icon: 'file-alt',
    helpHtml: 'عمق، مفید بودن و کیفیت کلی محتوای صفحه برای کاربر و موتور جستجو.'
  },
  'عنوان متا': {
    icon: 'tag',
    helpHtml: 'وضعیت تگ عنوان صفحه از نظر طول، کلیدواژه و جذابیت کلیک.'
  },
  'توضیحات متا': {
    icon: 'align-left',
    helpHtml: 'کیفیت و کامل بودن توضیحات متا برای نمایش در نتایج جستجو.'
  },
  'تگ H1': {
    icon: 'heading',
    helpHtml: 'وجود، یکتایی و هماهنگی تگ H1 با موضوع و کلمه کلیدی صفحه.'
  },
  'ساختار آدرس صفحه': {
    icon: 'link',
    helpHtml: 'خوانایی، کوتاه بودن و وجود کلیدواژه در ساختار URL صفحه.'
  },
  'متن جایگزین تصاویر (Alt)': {
    icon: 'image',
    helpHtml: 'وجود و کیفیت متن جایگزین تصاویر برای دسترسی‌پذیری و سئو.'
  }
};

const DEFAULT_CRITERION_META = {
  icon: 'check' as IconProp,
  helpHtml: 'امتیاز این معیار سئو از ۱۰ برای مقایسه وضعیت صفحه.'
};

const KpiLabel = ({ title, helpHtml }: { title: string; helpHtml: string }) => (
  <span className="d-inline-flex align-items-center gap-1">
    {title}
    <HelpTooltip html={helpHtml} />
  </span>
);

const getTopSeoCriteria = (items: SeoStructureItem[]) => {
  const picked = TOP_SEO_CRITERIA.map(name =>
    items.find(item => item.name === name)
  ).filter((item): item is SeoStructureItem => Boolean(item));

  if (picked.length >= 6) return picked.slice(0, 6);

  const extras = items.filter(
    item =>
      !TOP_SEO_CRITERIA.includes(item.name) &&
      item.name !== 'نشانه‌گذاری اسکیما' &&
      item.name !== 'Schema Markup'
  );
  return [...picked, ...extras].slice(0, 6);
};

const HEALTH_ICONS: Record<SeoPriorityTone, IconProp> = {
  danger: 'exclamation-triangle',
  warning: 'bolt',
  info: 'info-circle',
  success: 'check'
};

const LikenessSplit = ({
  label,
  value,
  icon,
  iconClassName,
  iconSize = '0.85em',
  helpHtml
}: {
  label: string;
  value: number;
  icon: IconProp;
  iconClassName?: string;
  iconSize?: string;
  helpHtml: string;
}) => (
  <div className="d-flex justify-content-between align-items-center bg-100 border rounded-3 px-3 py-2">
    <span className="d-inline-flex align-items-center gap-1 fs--1 text-600 lh-1">
      <FontAwesomeIcon
        icon={icon}
        className={`d-block flex-shrink-0 ${iconClassName || ''}`}
        style={{ width: iconSize, height: iconSize }}
      />
      <span className="lh-1">{label}</span>
      <HelpTooltip placement="top" html={helpHtml} />
    </span>
    <span className="fw-bold fs-0 lh-1">
      {toPersianDigit(`${Math.round(value)}%`)}
    </span>
  </div>
);

const LikenessSplitPending = ({
  label,
  icon,
  iconClassName,
  iconSize = '0.85em',
  helpHtml
}: {
  label: string;
  icon: IconProp;
  iconClassName?: string;
  iconSize?: string;
  helpHtml: string;
}) => (
  <div className="d-flex justify-content-between align-items-center bg-100 border rounded-3 px-3 py-2">
    <span className="d-inline-flex align-items-center gap-1 fs--1 text-600 lh-1">
      <FontAwesomeIcon
        icon={icon}
        className={`d-block flex-shrink-0 ${iconClassName || ''}`}
        style={{ width: iconSize, height: iconSize }}
      />
      <span className="lh-1">{label}</span>
      <HelpTooltip placement="top" html={helpHtml} />
    </span>
    <TextLoading width={28} height={12} isWidthPx />
  </div>
);

const SeoHealthList = ({
  items
}: {
  items: SeoOverviewData['health_items'];
}) => (
  <div className="d-flex flex-column gap-2 w-100 flex-grow-1">
    {items.map(item => (
      <div
        key={item.category}
        className="d-flex align-items-start gap-3 bg-100 rounded-3 px-3 py-2 border position-relative overflow-hidden"
      >
        <span
          className={`position-absolute top-0 bottom-0 bg-${item.tone}`}
          style={{ width: 3, insetInlineStart: 0 }}
        />
        <span
          className={`d-flex align-items-center justify-content-center rounded-circle bg-soft-${item.tone} text-${item.tone} flex-shrink-0`}
          style={{ width: '1.75rem', height: '1.75rem' }}
        >
          <FontAwesomeIcon icon={HEALTH_ICONS[item.tone]} className="fs--2" />
        </span>
        <div className="min-w-0 flex-grow-1">
          <div className="d-flex align-items-center justify-content-between gap-2">
            <span className="d-inline-flex align-items-center min-w-0">
              <span className="fw-semi-bold fs--1 text-truncate lh-sm">
                {item.category}
              </span>
              <HelpTooltip placement="top" html={item.finding} />
            </span>
            <SubtleBadge bg={item.tone} className="fs--2 flex-shrink-0">
              {item.priority}
            </SubtleBadge>
          </div>
          <div className="fs--2 text-600 lh-sm mt-1">{item.finding}</div>
        </div>
      </div>
    ))}
  </div>
);

const DetectionPendingBadge = () => (
  <SubtleBadge bg="info" pill className="d-inline-flex align-items-center gap-1">
    <Spinner
      animation="border"
      className="flex-shrink-0"
      style={{ width: 10, height: 10, borderWidth: 1.5 }}
    />
    در حال دریافت
  </SubtleBadge>
);

const ContentLikenessPending = () => (
  <Card.Body className="ai-detection-pending-body">
    <div className="position-relative">
      <SemiCircleChartSkeleton title="" tooltip="" minHeight="180px" />
      <div className="ai-detection-pending-overlay text-center px-3">
        <div className="ai-detection-pending-pulse mb-2">
          <Spinner animation="border" variant="primary" size="sm" />
        </div>
        <div className="fw-semi-bold fs--1 text-700">
          در حال تحلیل شباهت محتوا
        </div>
        <div className="fs--2 text-500 mt-1">
          امتیازها به‌محض آماده‌شدن اینجا نمایش داده می‌شوند
        </div>
      </div>
    </div>
    <div className="text-center mb-3 mt-2">
      <div className="d-flex justify-content-center">
        <TextLoading width={42} height={12} />
      </div>
      <div className="mt-2 d-flex justify-content-center">
        <DetectionPendingBadge />
      </div>
    </div>
    <Row className="g-2">
      <Col xs={6}>
        <LikenessSplitPending
          label="انسان"
          icon="user"
          iconClassName="text-success"
          helpHtml="سهم الگوهای نوشتاری که بیشتر به محتوای انسانی شباهت دارند."
        />
      </Col>
      <Col xs={6}>
        <LikenessSplitPending
          label="AI"
          icon="robot"
          iconClassName="text-warning"
          iconSize="1.05em"
          helpHtml="سهم الگوهای نوشتاری که بیشتر به محتوای تولیدشده توسط AI شباهت دارند."
        />
      </Col>
    </Row>
  </Card.Body>
);


const EeatOverviewPending = () => (
  <Card.Body className="ai-detection-pending-body d-flex flex-column flex-grow-1">
    <div className="position-relative flex-grow-1 d-flex flex-column justify-content-center">
      <SemiCircleChartSkeleton
        title=""
        tooltip=""
        minHeight="160px"
        styles={{
          borderRadius: '12rem 12rem 0 0',
          width: '58%',
          height: '120px',
          margin: '8px auto 4px'
        }}
      />
      <div className="ai-detection-pending-overlay text-center px-3">
        <div className="ai-detection-pending-pulse mb-2">
          <Spinner animation="border" variant="primary" size="sm" />
        </div>
        <div className="fw-semi-bold fs--1 text-700">در حال تحلیل E-E-A-T</div>
        <div className="fs--2 text-500 mt-1">
          امتیازها به‌محض آماده‌شدن اینجا نمایش داده می‌شوند
        </div>
      </div>
    </div>
    <div className="text-center mb-3 mt-2">
      <div className="d-flex justify-content-center">
        <TextLoading width={42} height={12} />
      </div>
      <div className="mt-2 d-flex justify-content-center">
        <DetectionPendingBadge />
      </div>
    </div>
    <Row className="g-2 mt-auto">
      <Col xs={6}>
        <LikenessSplitPending
          label="نقاط قوت"
          icon="trophy"
          iconClassName="text-success"
          helpHtml="تعداد نقاط قوت کلیدی شناسایی‌شده در ارزیابی E-E-A-T."
        />
      </Col>
      <Col xs={6}>
        <LikenessSplitPending
          label="شکاف‌ها"
          icon="exclamation-triangle"
          iconClassName="text-warning"
          helpHtml="تعداد شکاف‌های کلیدی که تجربه، تخصص یا اعتماد صفحه را تضعیف می‌کنند."
        />
      </Col>
    </Row>
  </Card.Body>
);

const GeoOverviewPending = () => (
  <Card.Body className="ai-detection-pending-body d-flex flex-column flex-grow-1">
    <div className="position-relative flex-grow-1 d-flex flex-column justify-content-center">
      <SemiCircleChartSkeleton
        title=""
        tooltip=""
        minHeight="160px"
        styles={{
          borderRadius: '12rem 12rem 0 0',
          width: '58%',
          height: '120px',
          margin: '8px auto 4px'
        }}
      />
      <div className="ai-detection-pending-overlay text-center px-3">
        <div className="ai-detection-pending-pulse mb-2">
          <Spinner animation="border" variant="primary" size="sm" />
        </div>
        <div className="fw-semi-bold fs--1 text-700">در حال تحلیل GEO</div>
        <div className="fs--2 text-500 mt-1">
          امتیازها به‌محض آماده‌شدن اینجا نمایش داده می‌شوند
        </div>
      </div>
    </div>
    <div className="text-center mb-3 mt-2">
      <div className="d-flex justify-content-center">
        <TextLoading width={42} height={12} />
      </div>
      <div className="mt-2 d-flex justify-content-center">
        <DetectionPendingBadge />
      </div>
    </div>
    <Row className="g-2 mt-auto">
      <Col xs={6}>
        <LikenessSplitPending
          label="نقاط قوت"
          icon="trophy"
          iconClassName="text-success"
          helpHtml="تعداد نقاط قوت کلیدی شناسایی‌شده برای جستجوی مولد."
        />
      </Col>
      <Col xs={6}>
        <LikenessSplitPending
          label="شکاف‌ها"
          icon="exclamation-triangle"
          iconClassName="text-warning"
          helpHtml="تعداد شکاف‌هایی که آمادگی صفحه برای جستجوی مبتنی بر هوش مصنوعی را تضعیف می‌کنند."
        />
      </Col>
    </Row>
  </Card.Body>
);

const AiAnalysisOverview = ({
  seo,
  detector,
  eeat = null,
  geo = null,
  showDetection = true,
  isDetectionPending = false,
  isEeatPending = false,
  isGeoPending = false
}: AiAnalysisOverviewProps) => {
  const { slug, domain_slug, domain } = useParams();
  const topCriteria = seo ? getTopSeoCriteria(seo.structure) : [];
  const analysisSlug = slug && slug !== 'last' ? slug : 'last';
  const showEeat = Boolean(eeat) || isEeatPending;
  const eeatPending = isEeatPending && !eeat;
  const eeatCardClassName = 'h-100 d-flex flex-column';
  const showGeo = Boolean(geo) || isGeoPending;
  const geoPending = isGeoPending && !geo;
  const geoCardClassName = 'h-100 d-flex flex-column';
  const eeatDetailsHref = aiAnalysisDetailsPath(
    'eeat',
    domain_slug || '',
    domain || '',
    analysisSlug
  );
  const geoDetailsHref = aiAnalysisDetailsPath(
    'geo',
    domain_slug || '',
    domain || '',
    analysisSlug
  );
  const seoDetailsHref = aiAnalysisDetailsPath(
    'seo',
    domain_slug || '',
    domain || '',
    analysisSlug
  );
  const detectorDetailsHref = aiAnalysisDetailsPath(
    'detector',
    domain_slug || '',
    domain || '',
    analysisSlug
  );

  return (
    <>
      {seo ? (
        <>
          <OverviewSectionTitle
            icon="search"
            title="تحلیل سئو"
            subtitle="وضعیت سئوی محتوایی و فنی صفحه در یک نگاه"
            helpHtml="جزئیات امتیاز سئو، معیارهای محتوایی و فنی صفحه و یافته‌های سلامت سئو."
            className="mb-3 mt-4"
            action={
              <Link to={seoDetailsHref} className="fs--2 fw-medium text-primary">
                مشاهده گزارش ←
              </Link>
            }
          />
          <Row className="g-3 mb-4">
            <Col xs={12} sm={6} lg={3}>
              <KpiStatCard
                imageIndex={0}
                label={
                  <KpiLabel
                    title="امتیاز کلی سئو"
                    helpHtml="امتیاز نهایی سئوی صفحه از مجموع معیارهای محتوایی و فنی."
                  />
                }
                value={toPersianDigit(seo.overall_score.toFixed(1))}
                valueClassName="text-success"
                valueBadge={
                  <SoftBadge bg="success" pill className="fs--2">
                    {seo.verdict}
                  </SoftBadge>
                }
                subtext={`از ${toPersianDigit(`${seo.overall_max}`)}`}
              />
            </Col>
            <Col xs={12} sm={6} lg={3}>
              <KpiStatCard
                imageIndex={1}
                label={
                  <KpiLabel
                    title="نقاط قوت"
                    helpHtml="تعداد مواردی که در صفحه به‌خوبی رعایت شده‌اند."
                  />
                }
                value={toPersianDigit(`${seo.strengths_count}`)}
                valueClassName="text-info"
                subtext="موارد بهینه در صفحه"
              />
            </Col>
            <Col xs={12} sm={6} lg={3}>
              <KpiStatCard
                imageIndex={2}
                label={
                  <KpiLabel
                    title="نقاط ضعف"
                    helpHtml="تعداد مواردی که نیاز به اصلاح دارند."
                  />
                }
                value={toPersianDigit(`${seo.weaknesses_count}`)}
                valueClassName="text-warning"
                subtext="موارد نیازمند اصلاح"
              />
            </Col>
            <Col xs={12} sm={6} lg={3}>
              <KpiStatCard
                imageIndex={3}
                label={
                  <KpiLabel
                    title="پیشنهاد بهبود"
                    helpHtml="تعداد اقدام‌های قابل اجرا برای بهتر شدن سئوی صفحه."
                  />
                }
                value={toPersianDigit(`${seo.suggestions_count}`)}
                valueClassName="text-primary"
                subtext="اقدام قابل اجرا"
              />
            </Col>
          </Row>
          <Row className="g-3 mb-4">
            <Col xs={12} lg={4}>
              <SeoScoreCard
                data={seo}
                className="h-100 d-flex flex-column"
                showCriteria={false}
              />
            </Col>

            <Col xs={12} lg={4}>
              <Card className="h-100 d-flex flex-column">
                <FalconCardHeader
                  title={
                    <OverviewCardTitle
                      icon="tasks"
                      title="امتیاز معیارهای سئو"
                      helpHtml="امتیاز جداگانه هر معیار سئو از ۱۰ برای مقایسه وضعیت بخش‌های مختلف صفحه."
                    />
                  }
                  titleTag="h6"
                  titleClass="d-flex align-items-center"
                  className="py-2"
                  light
                />
                <Card.Body className="py-3 flex-grow-1 d-flex">
                  <div className="seo-criteria-overview-list d-flex flex-column justify-content-between w-100">
                    {topCriteria.map(item => {
                      const meta =
                        SEO_CRITERIA_META[item.name] || DEFAULT_CRITERION_META;
                      return (
                        <div key={item.name}>
                          <div className="d-flex justify-content-between fs--1 mb-2 gap-2">
                            <OverviewItemTitle
                              icon={meta.icon}
                              title={item.name}
                              helpHtml={meta.helpHtml}
                            />
                            <span className="fw-semi-bold flex-shrink-0">
                              {toPersianDigit(`${item.score}`)} / ۱۰
                            </span>
                          </div>
                          <ProgressBar
                            now={item.score * 10}
                            variant={
                              item.score >= 9
                                ? 'success'
                                : item.score >= 7
                                  ? 'info'
                                  : 'warning'
                            }
                            style={{ height: 6 }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} lg={4}>
              <Card className="h-100 d-flex flex-column">
                <FalconCardHeader
                  title={
                    <OverviewCardTitle
                      icon="clipboard-check"
                      title="سلامت سئو"
                      helpHtml="یافته‌ها و اولویت‌های بهبود سلامت سئوی صفحه، از موارد فوری تا فرصت‌های بعدی."
                    />
                  }
                  titleTag="h6"
                  titleClass="d-flex align-items-center"
                  className="py-2"
                  light
                  endEl={
                    <SubtleBadge bg="primary">
                      {toPersianDigit(`${seo.health_items.length}`)}
                    </SubtleBadge>
                  }
                />
                <Card.Body className="py-2 flex-grow-1 d-flex">
                  <SeoHealthList items={seo.health_items} />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      ) : null}

      {showDetection ? (
        <>
          <OverviewSectionTitle
            icon="robot"
            title="تشخیص AI"
            subtitle="میزان شباهت نوشتار به الگوی انسانی و ماشینی"
            helpHtml="این بخش الگوهای نوشتاری صفحه را با محتوای انسانی و تولیدشده توسط AI مقایسه می‌کند و تشخیص قطعی نویسنده نیست."
            action={
              <Link
                to={detectorDetailsHref}
                className="fs--2 fw-medium text-primary"
              >
                مشاهده گزارش ←
              </Link>
            }
          />
          <Row className="g-3 mb-4">
            <Col xs={12} xl={4}>
              <Card className="h-100">
                <FalconCardHeader
                  title={
                    <OverviewCardTitle
                      icon="balance-scale-left"
                      title="شباهت محتوا"
                      helpHtml="این امتیاز نشان‌دهنده میزان شباهت الگوهای نوشتاری به محتوای تولیدشده توسط AI است و به معنی تشخیص قطعی نویسنده متن نیست."
                    />
                  }
                  titleTag="h6"
                  titleClass="d-flex align-items-center"
                  className="py-2"
                  light
                  endEl={
                    isDetectionPending ? <DetectionPendingBadge /> : undefined
                  }
                />
                {isDetectionPending ? (
                  <ContentLikenessPending />
                ) : (
                  <Card.Body>
                    <AiLikenessGauge
                      value={detector.human_likeness}
                      displayValue={toPersianDigit(
                        `${Math.round(detector.human_likeness)}`
                      )}
                    />
                    <div className="text-center mb-4 ai-likeness-gauge-caption">
                      <div className="fw-bold fs-0">{detector.verdict}</div>
                      <SubtleBadge bg="success" pill className="mt-2">
                        اطمینان {detector.confidence}
                      </SubtleBadge>
                    </div>
                    <Row className="g-2">
                      <Col xs={6}>
                        <LikenessSplit
                          label="انسان"
                          icon="user"
                          iconClassName="text-success"
                          value={detector.human_likeness}
                          helpHtml="سهم الگوهای نوشتاری که بیشتر به محتوای انسانی شباهت دارند."
                        />
                      </Col>
                      <Col xs={6}>
                        <LikenessSplit
                          label="AI"
                          icon="robot"
                          iconClassName="text-warning"
                          iconSize="1.05em"
                          value={detector.ai_likeness}
                          helpHtml="سهم الگوهای نوشتاری که بیشتر به محتوای تولیدشده توسط AI شباهت دارند."
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                )}
              </Card>
            </Col>

            <AiSignalsPanel
              humanSignals={detector.human_signals}
              aiSignals={detector.ai_signals}
              isPending={isDetectionPending}
            />
          </Row>
        </>
      ) : null}

      {showEeat ? (
        <>
          <OverviewSectionTitle
            icon="award"
            title="تحلیل E-E-A-T"
            subtitle="تجربه، تخصص، اعتبار و اعتماد صفحه"
            helpHtml="ارزیابی کیفیت تجربه، تخصص، اعتبار و اعتماد صفحه بر اساس چارچوب E-E-A-T گوگل."
            action={
              <Link
                to={eeatDetailsHref}
                className="fs--2 fw-medium text-primary"
              >
                مشاهده گزارش ←
              </Link>
            }
          />
          <Row className="g-3 mb-4">
            <Col xs={12} lg={4}>
              <Card className={eeatCardClassName}>
                <FalconCardHeader
                  title={
                    <OverviewCardTitle
                      icon="award"
                      title="امتیاز E-E-A-T"
                      helpHtml="این امتیاز کیفیت تجربه، تخصص، اعتبار و اعتماد صفحه را بر اساس چارچوب E-E-A-T نشان می‌دهد."
                    />
                  }
                  titleTag="h6"
                  titleClass="d-flex align-items-center"
                  className="py-2"
                  light
                  endEl={isEeatPending ? <DetectionPendingBadge /> : undefined}
                />
                {eeatPending ? (
                  <EeatOverviewPending />
                ) : eeat ? (
                  <Card.Body className="d-flex flex-column flex-grow-1">
                    <EeatScoreSummary data={eeat} metricsClassName="mt-auto" />
                  </Card.Body>
                ) : null}
              </Card>
            </Col>

            <Col xs={12} lg={4}>
              <Card className={eeatCardClassName}>
                <FalconCardHeader
                  title={
                    <OverviewCardTitle
                      icon="layer-group"
                      title="ابعاد E-E-A-T"
                      helpHtml="امتیاز جداگانه تجربه، تخصص، اعتبار و اعتماد صفحه."
                    />
                  }
                  titleTag="h6"
                  titleClass="d-flex align-items-center"
                  className="py-2"
                  light
                  endEl={isEeatPending ? <DetectionPendingBadge /> : undefined}
                />
                <Card.Body className="py-2 px-3 flex-grow-1 d-flex flex-column">
                  {eeatPending ? (
                    <EeatDimensionsOverviewPending />
                  ) : eeat && eeat.dimensions.length > 0 ? (
                    <EeatDimensionsOverview dimensions={eeat.dimensions} />
                  ) : (
                    <p className="text-600 mb-0 fs--1">
                      ابعادی برای نمایش وجود ندارد.
                    </p>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} lg={4}>
              <Card className={eeatCardClassName}>
                <FalconCardHeader
                  title={
                    <OverviewCardTitle
                      icon="clipboard-check"
                      title="خلاصه E-E-A-T"
                      helpHtml="مهم‌ترین نقاط قوت و شکاف‌های اعتماد، تخصص و تجربه صفحه."
                    />
                  }
                  titleTag="h6"
                  titleClass="d-flex align-items-center"
                  className="py-2"
                  light
                  endEl={isEeatPending ? <DetectionPendingBadge /> : undefined}
                />
                <Card.Body className="py-2 flex-grow-1 d-flex flex-column">
                  {eeatPending ? (
                    <div className="d-flex flex-column gap-2 flex-grow-1">
                      <div className="bg-100 border rounded-3 p-3 flex-grow-1">
                        <TextLoading width={36} height={10} />
                        <div className="mt-2 d-flex flex-column gap-2">
                          <TextLoading width={92} height={8} />
                          <TextLoading width={78} height={8} />
                        </div>
                      </div>
                      <div className="bg-100 border rounded-3 p-3 flex-grow-1">
                        <TextLoading width={32} height={10} />
                        <div className="mt-2 d-flex flex-column gap-2">
                          <TextLoading width={88} height={8} />
                          <TextLoading width={70} height={8} />
                        </div>
                      </div>
                    </div>
                  ) : eeat ? (
                    <div className="d-flex flex-column gap-2">
                      {eeat.top_strengths.length > 0 ? (
                        <div className="bg-100 border rounded-3 p-3">
                          <div className="fs--1 fw-semi-bold text-700 mb-2">
                            <OverviewItemTitle
                              icon="check-circle"
                              title="نقاط قوت کلیدی"
                              helpHtml="مهم‌ترین نشانه‌های تجربه، تخصص و اعتماد که در صفحه دیده شده است."
                              iconClassName="text-success"
                            />
                          </div>
                          <ul className="list-unstyled mb-0">
                            {eeat.top_strengths.slice(0, 3).map(item => (
                              <li
                                key={item}
                                className="d-flex align-items-start gap-2 fs--1 text-700 mb-1"
                              >
                                <FontAwesomeIcon
                                  icon="check-circle"
                                  className="text-success mt-1 flex-shrink-0"
                                />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      {eeat.top_gaps.length > 0 ? (
                        <div className="bg-100 border rounded-3 p-3">
                          <div className="fs--1 fw-semi-bold text-700 mb-2">
                            <OverviewItemTitle
                              icon="exclamation-circle"
                              title="شکاف‌های کلیدی"
                              helpHtml="مهم‌ترین کمبودهایی که اعتماد، تخصص یا تجربه صفحه را تضعیف می‌کنند."
                              iconClassName="text-warning"
                            />
                          </div>
                          <ul className="list-unstyled mb-0">
                            {eeat.top_gaps.slice(0, 3).map(item => (
                              <li
                                key={item}
                                className="d-flex align-items-start gap-2 fs--1 text-700 mb-1"
                              >
                                <FontAwesomeIcon
                                  icon="exclamation-circle"
                                  className="text-warning mt-1 flex-shrink-0"
                                />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      {eeat.top_strengths.length === 0 &&
                      eeat.top_gaps.length === 0 ? (
                        <p className="text-600 mb-0 fs--1">
                          خلاصه‌ای برای نمایش وجود ندارد.
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      ) : null}

      {showGeo ? (
        <>
          <OverviewSectionTitle
            icon="globe"
            title="تحلیل GEO"
            subtitle="آمادگی صفحه برای جستجوی مولد و استنادپذیری در پاسخ‌های هوش مصنوعی"
            helpHtml="این بخش آمادگی صفحه برای نمایش یا استناد در جستجوی مبتنی بر هوش مصنوعی مانند AI Overview گوگل، ChatGPT Search و Perplexity را ارزیابی می‌کند."
            action={
              <Link
                to={geoDetailsHref}
                className="fs--2 fw-medium text-primary"
              >
                مشاهده گزارش ←
              </Link>
            }
          />
          <Row className="g-3 mb-4">
            <Col xs={12} lg={4}>
              <Card className={geoCardClassName}>
                <FalconCardHeader
                  title={
                    <OverviewCardTitle
                      icon="globe"
                      title="امتیاز GEO"
                      helpHtml="این امتیاز آمادگی قابل مشاهده صفحه برای جستجوی مولد را نشان می‌دهد و احتمال استناد در یک پلتفرم خاص را پیش‌بینی نمی‌کند."
                    />
                  }
                  titleTag="h6"
                  titleClass="d-flex align-items-center"
                  className="py-2"
                  light
                  endEl={isGeoPending ? <DetectionPendingBadge /> : undefined}
                />
                {geoPending ? (
                  <GeoOverviewPending />
                ) : geo ? (
                  <Card.Body className="d-flex flex-column flex-grow-1">
                    <GeoScoreSummary data={geo} metricsClassName="mt-auto" />
                  </Card.Body>
                ) : null}
              </Card>
            </Col>

            <Col xs={12} lg={4}>
              <Card className={geoCardClassName}>
                <FalconCardHeader
                  title={
                    <OverviewCardTitle
                      icon="layer-group"
                      title="ابعاد GEO"
                      helpHtml="امتیاز جداگانه استخراج‌پذیری، ساختار پاسخ، شواهد، اعتبار و دسترسی فنی صفحه."
                    />
                  }
                  titleTag="h6"
                  titleClass="d-flex align-items-center"
                  className="py-2"
                  light
                  endEl={isGeoPending ? <DetectionPendingBadge /> : undefined}
                />
                <Card.Body className="py-2 px-3 flex-grow-1 d-flex flex-column justify-content-center">
                  {geoPending ? (
                    <GeoDimensionsOverviewPending />
                  ) : geo && geo.dimensions.length > 0 ? (
                    <GeoDimensionsOverview dimensions={geo.dimensions} />
                  ) : (
                    <p className="text-600 mb-0 fs--1">
                      ابعادی برای نمایش وجود ندارد.
                    </p>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} lg={4}>
              <Card className={geoCardClassName}>
                <FalconCardHeader
                  title={
                    <OverviewCardTitle
                      icon="clipboard-check"
                      title="خلاصه GEO"
                      helpHtml="مهم‌ترین نقاط قوت و شکاف‌های آمادگی صفحه برای جستجوی مولد."
                    />
                  }
                  titleTag="h6"
                  titleClass="d-flex align-items-center"
                  className="py-2"
                  light
                  endEl={isGeoPending ? <DetectionPendingBadge /> : undefined}
                />
                <Card.Body className="py-2 flex-grow-1 d-flex flex-column">
                  {geoPending ? (
                    <div className="d-flex flex-column gap-2 flex-grow-1">
                      <div className="bg-100 border rounded-3 p-3 flex-grow-1">
                        <TextLoading width={36} height={10} />
                        <div className="mt-2 d-flex flex-column gap-2">
                          <TextLoading width={92} height={8} />
                          <TextLoading width={78} height={8} />
                        </div>
                      </div>
                      <div className="bg-100 border rounded-3 p-3 flex-grow-1">
                        <TextLoading width={32} height={10} />
                        <div className="mt-2 d-flex flex-column gap-2">
                          <TextLoading width={88} height={8} />
                          <TextLoading width={70} height={8} />
                        </div>
                      </div>
                    </div>
                  ) : geo ? (
                    <div className="d-flex flex-column gap-2">
                      {geo.top_strengths.length > 0 ? (
                        <div className="bg-100 border rounded-3 p-3">
                          <div className="fs--1 fw-semi-bold text-700 mb-2">
                            <OverviewItemTitle
                              icon="check-circle"
                              title="نقاط قوت کلیدی"
                              helpHtml="مهم‌ترین نشانه‌های آمادگی صفحه برای استخراج و استناد در جستجوی مولد."
                              iconClassName="text-success"
                            />
                          </div>
                          <ul className="list-unstyled mb-0">
                            {geo.top_strengths.slice(0, 3).map(item => (
                              <li
                                key={item}
                                className="d-flex align-items-start gap-2 fs--1 text-700 mb-1"
                              >
                                <FontAwesomeIcon
                                  icon="check-circle"
                                  className="text-success mt-1 flex-shrink-0"
                                />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      {geo.top_gaps.length > 0 ? (
                        <div className="bg-100 border rounded-3 p-3">
                          <div className="fs--1 fw-semi-bold text-700 mb-2">
                            <OverviewItemTitle
                              icon="exclamation-circle"
                              title="شکاف‌های کلیدی"
                              helpHtml="مهم‌ترین کمبودهایی که شانس دیده شدن در پاسخ‌های هوش مصنوعی را تضعیف می‌کنند."
                              iconClassName="text-warning"
                            />
                          </div>
                          <ul className="list-unstyled mb-0">
                            {geo.top_gaps.slice(0, 3).map(item => (
                              <li
                                key={item}
                                className="d-flex align-items-start gap-2 fs--1 text-700 mb-1"
                              >
                                <FontAwesomeIcon
                                  icon="exclamation-circle"
                                  className="text-warning mt-1 flex-shrink-0"
                                />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      {geo.top_strengths.length === 0 &&
                      geo.top_gaps.length === 0 ? (
                        <p className="text-600 mb-0 fs--1">
                          خلاصه‌ای برای نمایش وجود ندارد.
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      ) : null}
    </>
  );
};

export default AiAnalysisOverview;
